/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import {
    ArrowRight,
    ArrowUpRight,
    Plus,
    Minus,
    Building2,
    Truck,
    TrendingUp,
    Leaf,
    Globe2,
    ShieldCheck,
    ClipboardList,
    FileEdit,
    GitCompare,
    HandCoins,
    Check,
    X,
} from "lucide-react";
import Navbar from "../components/dmb/Navbar";
import Footer from "../components/dmb/Footer";
import Seo, { breadcrumbTrail } from "../components/dmb/Seo";

const FAMILIES = [
    { n: "01", Icon: Building2, title: "Financement corporate", desc: "Besoin en fonds de roulement, investissements, découvert autorisé, crédit renouvelable (RCF).", tags: ["BFR", "CAPEX", "Découvert", "RCF"] },
    { n: "02", Icon: Truck, title: "Crédit-bail et actifs", desc: "Matériel, véhicules, immobilier d'entreprise : le bon montage entre crédit, crédit-bail et location.", tags: ["Crédit-bail mobilier", "Immobilier", "Location"] },
    { n: "03", Icon: TrendingUp, title: "Croissance externe", desc: "Dette d'acquisition et refinancement de la cible. Nous structurons le financement, pas la transaction.", tags: ["Dette d'acquisition", "LBO", "Refinancement"] },
    { n: "04", Icon: Leaf, title: "Transition", desc: "Énergies renouvelables, efficacité énergétique, mobilité : les enveloppes dédiées et leurs conditions.", tags: ["ENR", "Rénovation", "Flottes"] },
    { n: "05", Icon: Globe2, title: "Trade finance", desc: "Sécuriser et financer vos flux à l'international, à l'import comme à l'export.", tags: ["Crédit documentaire", "Préfinancement export"] },
    { n: "06", Icon: ShieldCheck, title: "Garanties", desc: "Cautions de marché, garanties de restitution d'acompte et de bonne fin, en France et à l'international.", tags: ["Cautions", "Garanties à première demande"] },
];

const STEPS = [
    { n: "01", Icon: ClipboardList, title: "Diagnostic", desc: "Un rendez-vous de stratégie financière et bancaire pour cadrer le besoin, la structure adaptée et les établissements à consulter.", livrable: "Note de cadrage : montant, structure cible, calendrier" },
    { n: "02", Icon: FileEdit, title: "Dossier", desc: "Nous rédigeons le dossier de financement et construisons le prévisionnel avec vous et vos conseils (comptable, avocat, etc.).", livrable: "Dossier de financement et prévisionnel argumenté" },
    { n: "03", Icon: GitCompare, title: "Mise en concurrence", desc: "Nous présentons votre demande aux banques, leaseurs et fonds de dette pertinents, et nous suivons chaque interlocuteur.", livrable: "Tableau comparatif des offres reçues" },
    { n: "04", Icon: HandCoins, title: "Négociation et signature", desc: "Conditions, garanties, cautions personnelles, covenants : nous négocions chaque point jusqu'au déblocage des fonds.", livrable: "Offre signée et fonds débloqués" },
];

const COMPARE = [
    { sujet: "Préparation du dossier", seul: "Chaque banque demande son format", nous: "Un dossier unique, structuré pour les banques" },
    { sujet: "Mise en concurrence", seul: "Rendez-vous séparés, délais longs", nous: "Consultations en parallèle, calendrier maîtrisé" },
    { sujet: "Négociation", seul: "Négociation isolée, sans comparatif", nous: "Négociation sur base d'offres comparables" },
    { sujet: "Garanties et covenants", seul: "Clauses standardisées acceptées", nous: "Chaque clause relue et négociée" },
];

const FAQ = [
    { q: "À partir de quelle taille d'entreprise intervenez-vous ?", a: "Nous accompagnons les entreprises à partir de 2 M€ de chiffre d'affaires, et principalement les PME et ETI entre 5 et 500 M€." },
    { q: "Faut-il changer de banque ?", a: "Non. Nous consultons vos banques actuelles et, si c'est pertinent, d'autres établissements. L'objectif est le meilleur financement, pas un changement de banque." },
    { q: "Combien de temps dure une mission ?", a: "Cela dépend de l'opération. Le diagnostic permet de fixer un calendrier précis dès le premier rendez-vous." },
    { q: "Travaillez-vous avec notre expert-comptable ?", a: "Oui. Nous construisons le prévisionnel avec lui, et il reste votre interlocuteur sur les comptes." },
];

function FAQItem({ q, a, idx }) {
    return (
        <details data-testid={`faq-item-${idx}`} className="border-b border-dmb-border py-5 group">
            <summary className="list-none flex items-center justify-between gap-6 cursor-pointer">
                <span className="font-semibold text-dmb-navy text-[16px] lg:text-[17px]">{q}</span>
                <span className="w-9 h-9 rounded-full bg-dmb-page border border-dmb-border flex items-center justify-center text-dmb-navy shrink-0">
                    <Plus className="w-4 h-4 group-open:hidden" />
                    <Minus className="w-4 h-4 hidden group-open:block" />
                </span>
            </summary>
            <p className="mt-4 text-[15.5px] text-dmb-muted leading-[1.7] pr-14">{a}</p>
        </details>
    );
}

export default function FinancementPage() {
    return (
        <div data-testid="financement-page" className="bg-dmb-page">
            <Seo
                title="Financement PME-ETI : BFR, crédit-bail, LBO | DMB Partners"
                description="BFR, crédit-bail, croissance externe, trade finance, garanties : nous préparons le dossier, mettons les banques en concurrence et négocions chaque condition."
                path="/financement"
                jsonLd={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "FAQPage",
                            mainEntity: FAQ.map((f) => ({
                                "@type": "Question",
                                name: f.q,
                                acceptedAnswer: { "@type": "Answer", text: f.a },
                            })),
                        },
                        breadcrumbTrail([
                            { name: "Accueil", path: "/" },
                            { name: "Financement", path: "/financement" },
                        ]),
                    ],
                }}
            />
            <Navbar />
            <main>
                {/* Hero navy */}
                <section style={{ background: "#0F1B2D" }} className="text-white">
                    <div className="container-dmb pt-14 pb-16 lg:pt-24 lg:pb-24 max-w-[920px]">
                        <h1 className="text-white">
                            Obtenir le bon financement,{" "}
                            <span style={{ color: "#4EAE32" }}>aux bonnes conditions.</span>
                        </h1>
                        <p className="mt-7 text-[18px] lg:text-[20px] text-dmb-on-dark leading-[1.65] max-w-[780px]">
                            Nous construisons votre dossier comme une banque l'attend,
                            nous consultons les bons établissements et nous négocions
                            chaque condition, jusqu'au déblocage des fonds.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-3">
                            <Link to="/contact" data-testid="fin-cta-primary" className="btn-dmb btn-primary">
                                Présenter un projet
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a href="#methode" className="btn-dmb btn-outline-white">Notre méthode</a>
                        </div>
                    </div>
                </section>

                {/* Familles */}
                <section className="section-pad">
                    <div className="container-dmb">
                        <div className="max-w-[720px]">
                            <div className="surtitre mb-4">Ce que nous finançons</div>
                            <h2>Six familles de financement, un même niveau d'exigence.</h2>
                        </div>
                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            {FAMILIES.map((f) => (
                                <article key={f.n} data-testid={`family-${f.n}`} className="card-dmb p-6 flex flex-col">
                                    <div className="flex items-start justify-between mb-4">
                                        <f.Icon className="w-7 h-7 text-dmb-green-dark" strokeWidth={1.8} />
                                        <span className="surtitre">{f.n}</span>
                                    </div>
                                    <h3 className="text-[18px] font-bold text-dmb-navy">{f.title}</h3>
                                    <p className="mt-2.5 text-[14.5px] text-dmb-muted leading-[1.65] flex-1">{f.desc}</p>
                                    <div className="mt-5 flex flex-wrap gap-1.5">
                                        {f.tags.map((t) => (
                                            <span key={t} className="text-[12px] px-2.5 py-1 rounded-full bg-dmb-green-pale text-dmb-green-dark font-medium">{t}</span>
                                        ))}
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Méthode */}
                <section id="methode" className="section-pad bg-white border-t border-dmb-border scroll-mt-24">
                    <div className="container-dmb">
                        <div className="max-w-[720px]">
                            <div className="surtitre mb-4">Notre méthode</div>
                            <h2>Quatre étapes, un livrable à chaque fois.</h2>
                        </div>
                        <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
                            {STEPS.map((s) => (
                                <li key={s.n} data-testid={`step-${s.n}`} className="card-dmb p-7">
                                    <div className="flex items-center gap-4 mb-4">
                                        <span className="w-11 h-11 rounded-full bg-dmb-green-pale text-dmb-green-dark font-bold flex items-center justify-center">{s.n}</span>
                                        <s.Icon className="w-6 h-6 text-dmb-navy" strokeWidth={1.8} />
                                    </div>
                                    <h3 className="text-[20px] font-bold text-dmb-navy">{s.title}</h3>
                                    <p className="mt-3 text-[15px] text-dmb-muted leading-[1.65]">{s.desc}</p>
                                    <div className="mt-5 pt-5 border-t border-dmb-border-soft">
                                        <div className="surtitre text-[11px] mb-1">Livrable</div>
                                        <div className="text-dmb-navy text-[14px] font-medium">{s.livrable}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>

                {/* Comparatif */}
                <section className="section-pad border-t border-dmb-border">
                    <div className="container-dmb">
                        <div className="max-w-[720px]">
                            <div className="surtitre mb-4">La différence</div>
                            <h2>Seul face à vos banques, ou accompagné.</h2>
                        </div>
                        <div className="mt-10 overflow-hidden rounded-[18px] border border-dmb-border bg-white">
                            <table className="w-full text-[14.5px]" data-testid="compare-table">
                                <thead>
                                    <tr className="bg-dmb-page">
                                        <th className="text-left py-4 px-5 font-semibold text-dmb-navy w-1/3">Sujet</th>
                                        <th className="text-left py-4 px-5 font-semibold text-dmb-navy">Seul face à vos banques</th>
                                        <th className="text-left py-4 px-5 font-semibold text-dmb-navy">Avec DMB Partners</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARE.map((r) => (
                                        <tr key={r.sujet} className="border-t border-dmb-border-soft">
                                            <td className="py-4 px-5 text-dmb-navy font-medium">{r.sujet}</td>
                                            <td className="py-4 px-5 text-dmb-muted"><span className="inline-flex items-center gap-2"><X className="w-4 h-4 text-dmb-error shrink-0" />{r.seul}</span></td>
                                            <td className="py-4 px-5 text-dmb-navy"><span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-dmb-green-dark shrink-0" />{r.nous}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="section-pad bg-white border-t border-dmb-border">
                    <div className="container-dmb max-w-[920px]">
                        <h2>Questions fréquentes</h2>
                        <div className="mt-8">
                            {FAQ.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />)}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="section-pad border-t border-dmb-border">
                    <div className="container-dmb">
                        <div className="rounded-[20px] bg-dmb-navy text-white px-8 py-12 lg:px-14 lg:py-14 flex flex-col lg:flex-row gap-6 lg:gap-10 items-start lg:items-center justify-between">
                            <div className="max-w-[600px]">
                                <h2 className="text-white">Votre prochain financement se prépare maintenant.</h2>
                                <p className="mt-4 text-dmb-on-dark">Décrivez votre projet en deux minutes. Nous revenons vers vous sous 24 h ouvrées.</p>
                            </div>
                            <Link to="/contact" className="btn-dmb btn-primary">
                                Présenter un projet
                                <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
